<template>
    <div class="cron-schedule-editor">
        <b-form-group label="Schedule Type">
            <b-form-select v-model="scheduleType" :options="scheduleTypes" @change="updateCronExpression" />
        </b-form-group>

        <div v-if="scheduleType === 'custom'">
            <b-form-group label="Cron Expression">
                <b-form-input v-model="customCron" placeholder="* * * * *" @input="updateCustomCron" />
                <small class="text-muted">Format: minute hour day-of-month month day-of-week</small>
            </b-form-group>
        </div>

        <div v-else>
            <b-row>
                <b-col v-if="scheduleType === 'weekly'" cols="6">
                    <b-form-group label="Day of Week">
                        <b-form-select v-model="dayOfWeek" :options="daysOfWeek" @change="updateCronExpression" />
                    </b-form-group>
                </b-col>
                <b-col v-if="scheduleType === 'monthly'" cols="6">
                    <b-form-group label="Day of Month">
                        <b-form-select v-model="dayOfMonth" :options="daysOfMonth" @change="updateCronExpression" />
                    </b-form-group>
                </b-col>
                <b-col cols="6">
                    <b-form-group label="Time">
                        <b-form-input type="time" v-model="timeValue" @change="updateCronExpression" />
                    </b-form-group>
                </b-col>
            </b-row>
        </div>

        <div class="mt-2">
            <small class="text-muted">
                <strong>Generated Expression:</strong> {{ value || 'Not set' }}
            </small>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

@Component
export default class CronScheduleEditor extends Vue {
    @Prop({ default: "" }) value!: string;

    private scheduleType = "daily";
    private dayOfWeek = "1";
    private dayOfMonth = "1";
    private timeValue = "06:00";
    private customCron = "";

    private scheduleTypes = [
        { value: "daily", text: "Daily" },
        { value: "weekly", text: "Weekly" },
        { value: "monthly", text: "Monthly" },
        { value: "custom", text: "Custom Cron" }
    ];

    private daysOfWeek = [
        { value: "0", text: "Sunday" },
        { value: "1", text: "Monday" },
        { value: "2", text: "Tuesday" },
        { value: "3", text: "Wednesday" },
        { value: "4", text: "Thursday" },
        { value: "5", text: "Friday" },
        { value: "6", text: "Saturday" }
    ];

    private get daysOfMonth() {
        const days = [];
        for (let i = 1; i <= 28; i++) {
            days.push({ value: i.toString(), text: i.toString() });
        }
        days.push({ value: "L", text: "Last day of month" });
        return days;
    }

    mounted() {
        this.parseCronExpression();
    }

    @Watch("value")
    onValueChange() {
        this.parseCronExpression();
    }

    private parseCronExpression() {
        if (!this.value) return;

        const parts = this.value.split(" ");
        if (parts.length !== 5) {
            this.scheduleType = "custom";
            this.customCron = this.value;
            return;
        }

        const [minute, hour, dom, , dow] = parts;

        // Try to determine schedule type
        if (dom === "*" && dow === "*") {
            // Daily
            this.scheduleType = "daily";
            this.timeValue = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
        } else if (dom === "*" && dow !== "*") {
            // Weekly
            this.scheduleType = "weekly";
            this.dayOfWeek = dow;
            this.timeValue = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
        } else if (dom !== "*" && dow === "*") {
            // Monthly
            this.scheduleType = "monthly";
            this.dayOfMonth = dom;
            this.timeValue = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
        } else {
            // Custom
            this.scheduleType = "custom";
            this.customCron = this.value;
        }
    }

    private updateCronExpression() {
        const [hours, minutes] = this.timeValue.split(":");
        let cron = "";

        switch (this.scheduleType) {
            case "daily":
                cron = `${parseInt(minutes)} ${parseInt(hours)} * * *`;
                break;
            case "weekly":
                cron = `${parseInt(minutes)} ${parseInt(hours)} * * ${this.dayOfWeek}`;
                break;
            case "monthly":
                cron = `${parseInt(minutes)} ${parseInt(hours)} ${this.dayOfMonth} * *`;
                break;
            case "custom":
                cron = this.customCron;
                break;
        }

        this.$emit("input", cron);
    }

    private updateCustomCron() {
        this.$emit("input", this.customCron);
    }
}
</script>

<style scoped>
.cron-schedule-editor {
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 4px;
}
</style>
